import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    urlParamReplicaId,
    urlImportViewStructure,
    urlProjectFields,
    urlProjectFieldsSchema,
} from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { IFieldConfig } from '../project/documents/sliceProjectDocuments';
import { toBeginUpperCase } from '../../util/StringUtils';
import { safeJsonParse } from '../../util/DataUtils';
import { apiFetch } from '../../util/ProjectUtils';

export interface IImportStructureFields {
    IdDocIndex: string | null;
    Subject: string;
    IsSkeleton?: boolean;
}

export interface IImportViewDataState {
    fields: SlicePropsWithData<IFieldConfig[]>;
    fieldSchema: SlicePropsWithData<string[]>;
    viewStructures: SlicePropsWithData<{
        [key: string]: IImportStructureFields[];
    }>;
}

const initialState: IImportViewDataState = {
    fields: {
        data: [],
        loading: false,
        error: null,
    },
    fieldSchema: {
        data: [],
        loading: false,
        error: null,
    },
    viewStructures: {
        data: {},
        loading: false,
        error: null,
    },
};

const reducers = {};

const thunks = {
    getImportFields: async (config: { replicaId: string }): Promise<IFieldConfig[]> => {
        try {
            const { replicaId } = config;

            const url = urlProjectFields.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                method: 'GET',
                headers: {
                    viewName: 'import.default',
                },
            };

            const response = await apiFetch(url, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }
            return response.json();
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    getImportFieldSchema: async (config: { replicaId: string }): Promise<string[]> => {
        try {
            const { replicaId } = config;

            const url = urlProjectFieldsSchema.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                method: 'GET',
                headers: {
                    viewName: 'import.default',
                },
            };

            const response = await apiFetch(url, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }
            return response.json();
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    getImportStructure: async (config: {
        replicaId: string;
        viewName: string;
    }): Promise<[string, IImportStructureFields[]]> => {
        try {
            const { replicaId, viewName } = config;

            const url = urlImportViewStructure.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                method: 'GET',
                headers: {
                    viewName: toBeginUpperCase(viewName),
                },
            };

            const response = await apiFetch(url, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            const structure = safeJsonParse<IImportStructureFields[]>(await response.text(), []);

            return [viewName, structure];
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<IImportViewDataState>) => {
        builder.addCase(getImportFields.pending, (state: Draft<IImportViewDataState>) => {
            state.fields.loading = true;
            state.fields.error = null;
        });
        builder.addCase(
            getImportFields.fulfilled,
            (state: Draft<IImportViewDataState>, action: IAction<IFieldConfig[]>) => {
                state.fields.loading = false;
                state.fields.error = null;
                state.fields.data = action.payload;
            },
        );
        builder.addCase(
            getImportFields.rejected,
            (state: Draft<IImportViewDataState>, action: IAction<SerializedError>) => {
                state.fields.loading = false;
                state.fields.error = action.error;
                state.fields.data = [];
            },
        );

        builder.addCase(getImportFieldSchema.pending, (state: Draft<IImportViewDataState>) => {
            state.fieldSchema.loading = true;
            state.fieldSchema.error = null;
        });
        builder.addCase(
            getImportFieldSchema.fulfilled,
            (state: Draft<IImportViewDataState>, action: IAction<string[]>) => {
                state.fieldSchema.loading = false;
                state.fieldSchema.error = null;
                state.fieldSchema.data = action.payload;
            },
        );
        builder.addCase(
            getImportFieldSchema.rejected,
            (state: Draft<IImportViewDataState>, action: IAction<SerializedError>) => {
                state.fieldSchema.loading = false;
                state.fieldSchema.error = action.error;
                state.fieldSchema.data = [];
            },
        );

        builder.addCase(getImportStructure.pending, (state: Draft<IImportViewDataState>) => {
            state.viewStructures.loading = true;
            state.viewStructures.error = null;
        });
        builder.addCase(
            getImportStructure.fulfilled,
            (
                state: Draft<IImportViewDataState>,
                action: IAction<[string, IImportStructureFields[]]>,
            ) => {
                const [key, data] = action.payload;

                state.viewStructures.loading = false;
                state.viewStructures.error = null;
                state.viewStructures.data[key] = data;
            },
        );
        builder.addCase(
            getImportStructure.rejected,
            (state: Draft<IImportViewDataState>, action: IAction<SerializedError>) => {
                state.viewStructures.loading = false;
                state.viewStructures.error = action.error;
                state.viewStructures.data = {};
            },
        );
    },
};

export const getImportFields = createAsyncThunk('import/fields', thunks.getImportFields);
export const getImportFieldSchema = createAsyncThunk(
    'import/fieldschema',
    thunks.getImportFieldSchema,
);
export const getImportStructure = createAsyncThunk('import/structure', thunks.getImportStructure);

export const importViewDataSlice = createSlice({
    name: 'importViewData',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export default importViewDataSlice.reducer;
